import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import { UserProfile } from '../../types/api';
import CityAutocomplete from '../../components/Profile/shared/CityAutocomplete';
import AvatarUpload from '../../components/Profile/shared/AvatarUpload';
import { useToast } from '../../components/UI/Toast';
import { mediaUploadService } from '../../services/mediaUploadService';
import { buildOwnProfilePath } from '../../utils/share';
import { SelectTags, NewTagState } from '../../components/Tags/SelectTags';
import { deriveTagStateFromSource, flattenTagStateToNames } from '../../components/Tags/tagStateUtils';

const EditProfilePage = () => {
    const navigate = useNavigate();
    const { showToast, ToastWrapper } = useToast();
    const [loading, setLoading] = useState(true);
    const [avatarUrl, setAvatarUrl] = useState<string>('');
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

    const [formData, setFormData] = useState({
        displayName: '',
        userId: '',
        bio: '',
        email: '',
        phone: '',
        wechat: '',
        location: '',
        website: ''
    });
    const ownProfilePath = formData.userId ? buildOwnProfilePath(formData.userId) : '/profile';
    const [originalCreatorInfo, setOriginalCreatorInfo] = useState<any>(null);

    const [tagState, setTagState] = useState<NewTagState>({
        primaryCategory: null,
        secondaryCategories: [],
        toolTags: [],
        styleFacets: []
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await userService.getUserProfile();
                if (response.success && response.data?.userProfile) {
                    const profile = response.data.userProfile;
                    setAvatarUrl(profile.avatarUrl || '');
                    setFormData({
                        displayName: profile.displayName || '',
                        userId: profile.userId || '',
                        bio: profile.bio || '',
                        email: profile.email || '',
                        phone: profile.phone || '',
                        wechat: profile.wechat || '',
                        location: profile.location || '',
                        website: profile.website || ''
                    });
                    setOriginalCreatorInfo(profile.creatorInfo || null);
                    setTagState(deriveTagStateFromSource({ tags: profile.creatorInfo?.tags || [] }));
                }
            } catch (error) {
                console.error('Failed to fetch profile:', error);
                showToast('加载个人资料失败', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const updatePayload: Partial<UserProfile> & { tags?: string[] } = {
                displayName: formData.displayName,
                bio: formData.bio,
                wechat: formData.wechat,
                location: formData.location,
                website: formData.website,
                creatorInfo: {
                    ...(originalCreatorInfo || {}),
                    tags: flattenTagStateToNames(tagState),
                    skills: originalCreatorInfo?.skills || [],
                    portfolioHighlight: originalCreatorInfo?.portfolioHighlight || []
                }
            };

            const response = await userService.updateUserProfile(updatePayload);

            if (response.success) {
                showToast('个人资料已更新', 'success');
                setTimeout(() => navigate(ownProfilePath), 1000);
            } else {
                showToast('更新失败: ' + (response.error || 'Unknown error'), 'error');
            }
        } catch (error) {
            console.error('Update failed:', error);
            showToast('更新失败，请重试', 'error');
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">加载中...</div>;
    }

    return (
        <>
            <ToastWrapper />
            <div className="min-h-screen bg-white pt-24 pb-20">
                <div className="max-w-[800px] mx-auto px-6">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-8">
                        <Link to={ownProfilePath} className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors">
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-900">编辑个人资料</h1>
                    </div>

                    <div className="bg-white rounded-2xl animate-fade-in">
                        <form onSubmit={handleSubmit} className="space-y-6">

                            {/* Avatar Section */}
                            <AvatarUpload
                                currentAvatarUrl={avatarUrl}
                                onUpload={async (file) => {
                                    setIsUploadingAvatar(true);
                                    try {
                                        const uploadedUrl = await mediaUploadService.uploadFile(file, file.name, file.type);
                                        const result = await userService.updateAvatar(uploadedUrl);
                                        if (result.success) {
                                            const nextAvatarUrl = result.avatarUrl || avatarUrl;
                                            setAvatarUrl(nextAvatarUrl);
                                            showToast('头像已更新', 'success');
                                            return nextAvatarUrl || null;
                                        }
                                        showToast(result.error || '头像更新失败', 'error');
                                        return null;
                                    } catch (error) {
                                        console.error('Avatar upload error:', error);
                                        showToast('头像上传失败，请重试', 'error');
                                        return null;
                                    } finally {
                                        setIsUploadingAvatar(false);
                                    }
                                }}
                                isUploading={isUploadingAvatar}
                            />

                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">昵称</label>
                                    <input
                                        type="text"
                                        name="displayName"
                                        value={formData.displayName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2 flex justify-between">
                                        <span>用户id</span>
                                        <span className="text-xs text-gray-400 font-normal">系统分配，不可修改</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="userId"
                                        value={formData.userId}
                                        className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 font-mono cursor-not-allowed focus:outline-none"
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">所在城市</label>
                                    <CityAutocomplete
                                        value={formData.location}
                                        onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                                        name="location"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">个人简介</label>
                                <textarea
                                    name="bio"
                                    rows={4}
                                    value={formData.bio}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition-all resize-none"
                                />
                            </div>

                            {/* Professional Info */}
                            <div className="space-y-6 pt-2">
                                <h3 className="font-bold text-lg text-gray-900 border-b border-gray-100 pb-2">选择标签</h3>
                                <SelectTags
                                    value={tagState}
                                    onChange={setTagState}
                                    filterMode={true}
                                />
                            </div>

                            {/* Contact Info */}
                            <div className="space-y-6 pt-2">
                                <h3 className="font-bold text-lg text-gray-900 border-b border-gray-100 pb-2">联系方式</h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">邮箱</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition-all"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">手机号</label>
                                        <div className="flex gap-3">
                                            <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-500">
                                                {formData.phone || '未绑定'}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => showToast('短信验证功能开发中...', 'info')}
                                                className="px-4 py-2 border border-black rounded-xl font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                                            >
                                                {formData.phone ? '换绑' : '绑定'}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">微信号</label>
                                        <input
                                            type="text"
                                            name="wechat"
                                            value={formData.wechat}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition-all"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">个人网站</label>
                                        <input
                                            type="text"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8 flex justify-end gap-4 border-t border-gray-100 mt-8">
                                <button
                                    type="button"
                                    onClick={() => navigate(ownProfilePath)}
                                    className="px-6 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                                >
                                    取消
                                </button>
                                <button
                                    type="submit"
                                    className="px-8 py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center gap-2"
                                >
                                    <Save className="w-4 h-4" />
                                    保存更改
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditProfilePage;
